/**
 * CUE シートから FILE 参照を取り出す（軽量パーサ）。
 * 対応:
 *   FILE "name.bin" BINARY
 *   FILE 'name.bin' WAVE
 *   FILE name.bin BINARY
 *   FILE Track 1.bin BINARY   ← クォート無し・空白あり
 */

const FILE_LINE_QUOTED =
  /^\s*FILE\s+(?:"([^"]+)"|'([^']+)')\s*(?:BINARY|WAVE|MOTOROLA|AIFF)?/i;

/** クォート無し: 拡張子の直後までをファイル名とみなす */
const FILE_LINE_BARE =
  /^\s*FILE\s+(.+?\.(?:bin|img|iso|mdf|wav|aiff))\s*(?:BINARY|WAVE|MOTOROLA|AIFF)?/i;

/** CUE テキスト内のトラック実体ファイル名（出現順・重複除去）。 */
export function parseCueFileRefs(cueText: string): string[] {
  const refs: string[] = [];
  const seen = new Set<string>();
  for (const line of stripBom(cueText).split(/\r?\n/)) {
    const name = parseFileLine(line);
    if (!name) continue;
    const base = basename(name);
    const key = normalizeKey(base);
    if (seen.has(key)) continue;
    seen.add(key);
    refs.push(base);
  }
  return refs;
}

function parseFileLine(line: string): string | null {
  const q = FILE_LINE_QUOTED.exec(line);
  if (q) return (q[1] ?? q[2] ?? '').trim() || null;
  const b = FILE_LINE_BARE.exec(line);
  if (b) return (b[1] ?? '').trim() || null;
  return null;
}

/**
 * CUE 内の FILE 行を、実際に渡されたファイル名に書き換える。
 * 参照名と実ファイル名が食い違うダンプ向け。
 */
export function rewriteCueFileRefs(cueText: string, orderedNames: string[]): string {
  const text = stripBom(cueText);
  if (parseCueFileRefs(text).length === 0) {
    return orderedNames
      .map((n, idx) => {
        const track = String(idx + 1).padStart(2, '0');
        const mode = idx === 0 ? 'MODE2/2352' : 'AUDIO';
        return `FILE "${n}" BINARY\n  TRACK ${track} ${mode}\n    INDEX 01 00:00:00`;
      })
      .join('\n');
  }

  let i = 0;
  return text
    .split(/\r?\n/)
    .map((line) => {
      if (i >= orderedNames.length || !parseFileLine(line)) return line;
      const next = orderedNames[i++]!;
      const modeMatch = /\s+(BINARY|WAVE|MOTOROLA|AIFF)\s*$/i.exec(line);
      const mode = modeMatch?.[1] ?? 'BINARY';
      return `FILE "${next}" ${mode}`;
    })
    .join('\n');
}

export function stripBom(text: string): string {
  if (!text) return text;
  if (text.charCodeAt(0) === 0xfeff) return text.slice(1);
  return text;
}

export function basename(path: string): string {
  const normalized = path.replace(/\\/g, '/');
  const i = normalized.lastIndexOf('/');
  return i >= 0 ? normalized.slice(i + 1) : normalized;
}

export function isCueFilename(name: string): boolean {
  return /\.cue$/i.test(name);
}

export function isDiscDataFilename(name: string): boolean {
  return /\.(bin|img|iso|mdf)$/i.test(name);
}

/** 照合用キー（大小文字・全角半角・空白の揺れを吸収）。 */
export function normalizeKey(name: string): string {
  return basename(name)
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

/** ArrayBuffer を複数エンコーディングでテキスト化する。 */
export function decodeCueBuffer(buf: ArrayBuffer): string[] {
  const bytes = new Uint8Array(buf);
  const out: string[] = [];

  const push = (text: string) => {
    const t = stripBom(text);
    if (t && !out.includes(t)) out.push(t);
  };

  if (bytes.length >= 2) {
    if (bytes[0] === 0xff && bytes[1] === 0xfe) {
      try {
        push(new TextDecoder('utf-16le').decode(bytes));
      } catch {
        /* ignore */
      }
    } else if (bytes[0] === 0xfe && bytes[1] === 0xff) {
      try {
        push(new TextDecoder('utf-16be').decode(bytes));
      } catch {
        /* ignore */
      }
    }
  }

  try {
    push(new TextDecoder('utf-8', { fatal: false }).decode(bytes));
  } catch {
    /* ignore */
  }
  try {
    push(new TextDecoder('shift-jis', { fatal: false }).decode(bytes));
  } catch {
    /* ignore */
  }

  return out;
}
